import type { Optional } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'
import cx from 'classnames'
import type { ReactNode, Ref } from 'react'

import { FPlusOverflownCodeIndicator } from '../monaco/components/FPlusOverflownCodeIndicator'
import { useIsContentClipped } from './useIsContentClipped'

type Props<TElement extends HTMLElement> = {
  /**
   * The content in full, offered on hover only once the indicator has taken
   * the content's place.
   */
  tip: ReactNode
  /**
   * Optional label written across the indicator, for content whose meaning
   * still needs to be readable while it is covered.
   */
  title?: Optional<string>
  /**
   * Receives the ref that must go on the element that clips its content, which
   * is what decides whether the indicator is shown.
   */
  children: (ref: Ref<TElement>) => ReactNode
  className?: string
}

/**
 * Shows content that fits as it is, and replaces content that does not with an
 * indicator that reads as unmistakably truncated at a glance, offering the
 * content in full on hover.
 *
 * Clipping the content is the caller's job, and CSS's: this only measures the
 * result and reacts to it.
 */
export const IndicateOverflow = <TElement extends HTMLElement = HTMLElement>({
  tip,
  title,
  children,
  className,
}: Props<TElement>) => {
  const { contentRef, isContentClipped } = useIsContentClipped<TElement>()

  return (
    <Tooltip tip={isContentClipped ? tip : undefined} className="size-full">
      <div
        className={cx(
          'relative size-full',
          isContentClipped && 'cursor-help',
          className,
        )}
      >
        {/* Left in the layout, and so left measurable, so that the indicator
            covering it cannot measure the content away and flicker back off. */}
        <div
          className={cx(
            'flex size-full items-center',
            isContentClipped && 'invisible',
          )}
        >
          {children(contentRef)}
        </div>
        {isContentClipped && (
          <div className="absolute inset-0 flex items-center justify-center">
            <FPlusOverflownCodeIndicator title={title} />
          </div>
        )}
      </div>
    </Tooltip>
  )
}
